import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { fitTextToWidth } from "../motion/reveals";
import { BRANDS } from "./Nav";

// One list of season partners for the whole site (client, 2026-10-08): the
// footer grid shows exactly what the Ultimate Lap partners rail shows.
import { SEASON_PARTNERS } from "./PartnersBand";
const SPONSORS = SEASON_PARTNERS.map((l) => l.src);

/* `partners` (default true): the Season Partners grid at the top of the footer.
   The landing page passes false (client, 2026-10-07) - its logos moved up into
   the Manifesto band as "Brands we've worked with". Every other page keeps it. */
// Social links (client, 2026-10-10). Fill in the TikTok / YouTube URLs when shared.
const SOCIALS = [
  { id: "instagram", label: "Instagram", href: "https://www.instagram.com/gridmasters_ae/" },
  { id: "tiktok", label: "TikTok", href: null },
  { id: "youtube", label: "YouTube", href: null },
];

export default function Footer({ partners = true }) {
  const wordmarkRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!wordmarkRef.current || !containerRef.current) return;
    // Same lockup as the landing hero (mark + wordmark), fitted to the container.
    // measureText off: the lockup is inline-flex, so measure the whole box.
    const dispose = fitTextToWidth(wordmarkRef.current, {
      container: containerRef.current,
      minFontPx: 28,
      maxFontPx: 240,
      // Slightly under full width: the heavy display face overhangs its
      // measured box on the trailing "S", so a 1.0 fit clipped the glyph.
      paddingFactor: 0.975,
    });
    return dispose;
  }, []);

  return (
    <footer className="gm-footer" data-testid="site-footer" id={partners ? "partners" : undefined}>
      <div className="container-gm" ref={containerRef}>
        {/* No scroll reveals in the footer (client, 2026-09-19). The page
            clamps at the bottom, so anything still sliding into place here
            read as "the footer snaps back into its grid". Static. */}
        {partners && (
          <>
            <div className="gm-footer__sponsors">
              <div className="eyebrow" style={{ marginBottom: 24 }}>
                Season Partners
              </div>
              <div className="gm-footer__sponsor-row" data-testid="sponsor-row">
                {SPONSORS.map((s, i) => (
                  <div className="gm-footer__sponsor" key={i}>
                    <img src={s} alt="" loading="lazy" decoding="async" />
                  </div>
                ))}
              </div>
            </div>

            <div className="rule" style={{ margin: "56px 0" }} />
          </>
        )}

        <div className="gm-footer__cols">
          <div className="gm-footer__col">
            {/* Client, 2026-10-10: "Championships" -> "Disciplines". */}
            <div className="eyebrow">Disciplines</div>
            <ul>
              <li><Link to="/events/ultimate-lap" data-cursor-text="Ultimate Lap">Ultimate Lap</Link></li>
              <li><Link to="/events/speed-gates" data-cursor-text="Speed Gates">Speed Gates</Link></li>
              <li><Link to="/events/track-days" data-cursor-text="Track Days">Track Days</Link></li>
              <li><Link to="/events/corporate-events" data-cursor-text="Corporate Events">Corporate Events</Link></li>
            </ul>
          </div>
          <div className="gm-footer__col">
            <div className="eyebrow">Company</div>
            <ul>
              <li><Link to="/about">About Us</Link></li>
              {/* Client, 2026-10-07: Leaderboard link removed alongside the menu entry (route kept for later).
                  Client, 2026-10-10: Contact removed from Company (lives in the Contact column). */}
            </ul>
          </div>
          <div className="gm-footer__col">
            <div className="eyebrow">Connect</div>
            {/* Client, 2026-10-10: Instagram, TikTok, YouTube; Contact removed. TikTok and
                YouTube URLs still to come - until set in SOCIALS they render as plain text. */}
            <ul>
              {SOCIALS.map((s) => (
                <li key={s.id}>
                  {s.href ? (
                    <a href={s.href} target="_blank" rel="noopener noreferrer" data-cursor-text={s.label} data-testid={`footer-${s.id}`}>{s.label}</a>
                  ) : (
                    <span data-testid={`footer-${s.id}`} title="Link coming soon">{s.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="gm-footer__col gm-footer__col--meta">
            <div className="eyebrow">Contact</div>
            <p className="body">
              <Link to="/contact" data-cursor-text="Contact">Send us a message</Link>
              <br />
              <a href="mailto:info@gridmasters.ae" data-cursor-text="Email" data-testid="footer-email">info@gridmasters.ae</a>
            </p>
          </div>
        </div>

        {/* Client, 2026-09-09: footer signature = the hero lockup (logo left, GRID MASTERS wordmark). */}
        <div className="gm-footer__wordmark-track" aria-hidden="true">
          <span className="gm-lockup gm-footer__wordmark" ref={wordmarkRef} data-testid="footer-lockup">
            <img src={BRANDS.gridmasters.mark} alt="" className="gm-lockup__mark" loading="lazy" decoding="async" />
            <span className="gm-lockup__text">{BRANDS.gridmasters.text}</span>
          </span>
        </div>

        <div className="gm-footer__base">
          <span className="tech-num">© GRID MASTERS. ALL RIGHTS RESERVED.</span>
        </div>
      </div>
    </footer>
  );
}
